// ============================================================================
// amendementsCommun.js : la part LÉGÈRE de l'atelier d'amendements Thémis.
// ============================================================================
// Texte riche (assainissement du HTML collé, projection en texte simple) et
// abonnements en direct aux amendements et à leurs votes. Utilisés par
// l'écran de salle (App.jsx) et par l'affichage des points d'ODJ
// (appShared.js), donc présents dès le démarrage ; l'atelier lui-même
// (ThemisAmendements.js : éditeur, formulaires, diff, conflits) ne se charge
// plus qu'avec les vues qui le montrent (Refonte Mosaïque L6.0, 28/09/2026 :
// 7,7 Ko gz de moins sur le bundle principal). ThemisAmendements réexporte
// ce module : ses importeurs n'ont rien changé.
// ============================================================================
import { useState, useEffect, useMemo } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";

// ── Texte riche : conserver la mise en forme du copier-coller ───────────────
// On stocke le texte de base / consolidé en HTML assaini (liste blanche). Les
// passages/propositions des amendements restent en texte simple.
const RICH_TAGS = new Set(["P", "BR", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI", "H3", "H4", "DIV", "SPAN"]);
// Balises supprimées AVEC leur contenu (sinon le CSS d'un <style> Word, les
// scripts, les métadonnées <xml>/<head> collés depuis Word/Docs ressortiraient
// en texte brut dans l'éditeur).
const DROP_TAGS = new Set(["STYLE", "SCRIPT", "TITLE", "META", "LINK", "HEAD", "BASE", "NOSCRIPT", "XML", "OBJECT", "IFRAME"]);
export function isHtmlContent(s) { return !!s && /<(\/?)(p|br|ul|ol|li|strong|b|em|i|u|h3|h4|div|span)\b/i.test(s); }
export function escapeHtml(s) { return (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }
function _sanitizeInto(src, dst) {
  src.childNodes.forEach(child => {
    if (child.nodeType === 3) { dst.appendChild(document.createTextNode(child.nodeValue)); }
    else if (child.nodeType === 1) {
      const tag = child.tagName || "";
      // <style>, <script>, <head>, <xml>… : supprimés avec leur contenu (sinon
      // le CSS Word « /* Style Definitions */ » ressort en texte). Idem balises
      // Office à namespace (o:p, w:…, v:…) : on retire l'élément vide.
      if (DROP_TAGS.has(tag) || tag.includes(":")) return;
      if (RICH_TAGS.has(tag)) {
        const el = document.createElement(tag.toLowerCase()); // aucun attribut copié
        _sanitizeInto(child, el); dst.appendChild(el);
      } else { _sanitizeInto(child, dst); } // balise interdite → on garde le contenu (déballée)
    }
  });
}
// Assainit du HTML collé : ne garde que des balises de mise en forme, aucun
// attribut (pas de scripts/styles/handlers). Du texte simple est échappé.
/** Analyse un HTML dans un document INERTE (DOMParser) : rien n'y est chargé
 *  ni exécuté. Jamais un élément du document actif : un <img onerror> posé
 *  dans un div détaché y charge l'image et exécute le gestionnaire avant
 *  tout tri par la liste blanche (relecture adverse du 13/10/2026). */
function corpsInerte(html) {
  if (typeof DOMParser !== "undefined") return new DOMParser().parseFromString(String(html), "text/html").body;
  const d = document.implementation.createHTMLDocument("");
  d.body.innerHTML = String(html);
  return d.body;
}
export function sanitizeHtml(html) {
  if (!html) return "";
  if (typeof document === "undefined") return escapeHtml(html);
  if (!isHtmlContent(html)) return escapeHtml(html);
  const src = corpsInerte(html);
  const dst = document.createElement("div"); _sanitizeInto(src, dst);
  return dst.innerHTML;
}
// Projection texte simple (pour le diff, la détection de conflits, le repérage
// de passage). Le texte brut historique est renvoyé tel quel.
export function htmlToText(s) {
  if (!s) return "";
  if (typeof document === "undefined" || !isHtmlContent(s)) return s;
  const d = corpsInerte(s);
  return (d.textContent || "").replace(/ /g, " ");
}

// Mise en forme du texte riche AFFICHÉ (classe rich-text), injectée une fois.
// Celle de l'éditeur (rich-editor) reste avec l'éditeur, dans l'atelier.
if (typeof document !== "undefined" && !document.getElementById("rich-text-style")) {
  const st = document.createElement("style"); st.id = "rich-text-style";
  st.textContent = ".rich-text p{margin:0 0 calc(.5em * var(--em-ajuste, 1))}.rich-text ul,.rich-text ol{margin:calc(.2em * var(--em-ajuste, 1)) 0 calc(.5em * var(--em-ajuste, 1)) calc(1.2em * var(--em-ajuste, 1))}.rich-text h3,.rich-text h4{margin:calc(.3em * var(--em-ajuste, 1)) 0}";
  document.head.appendChild(st);
}

// ── Hooks temps réel ─────────────────────────────────────────────────────────
// Lot 4 des règles (18/10/2026) : les votes d'amendement (qui a voté quoi)
// ne se lisent plus depuis le navigateur ; chaque amendement porte le COMPTE
// de ses votes (`compte`, tenu par le serveur dans la transaction du vote).
export const compteDe = (a) => ({ pour: 0, contre: 0, abstention: 0, ...((a && a.compte) || {}) });
export function useAmendements(pointId) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!pointId) { setList([]); return; }
    const unsub = onSnapshot(query(collection(db, "themisAmendements"), where("pointId", "==", pointId)), (snap) => {
      setList(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(a => a.pointId === pointId)
        .sort((a, b) => (a.ts || 0) - (b.ts || 0)));
    }, (e) => console.warn("useAmendements:", e.message));
    return () => unsub();
  }, [pointId]);
  return list;
}

// Hook public pour l'écran de salle : abonne en direct aux propositions et
// agrège les votes par amendement (pour, contre, abstention), en lecture seule.
export function useAmendementsScreen(pointId) {
  const amendements = useAmendements(pointId);
  const votesByAmd = useMemo(() => {
    const m = {};
    for (const a of amendements) m[a.id] = compteDe(a);
    return m;
  }, [amendements]);
  const counts = useMemo(() => ({
    total: amendements.length,
    adopte: amendements.filter(a => a.statut === "adopte").length,
    enDelib: amendements.filter(a => !a.statut || a.statut === "en_delib").length,
    rejete: amendements.filter(a => a.statut === "rejete").length,
    retire: amendements.filter(a => a.statut === "retire").length,
  }), [amendements]);
  return { amendements, votesByAmd, counts };
}
